/**
 * A member may save profile details once after registration.
 * If name, surname, or phone is still missing, they may fill only those gaps.
 * After the required details are saved, only an admin may change them.
 * Display and API share this check. It does not write a balance or a share.
 */

export const PROFILE_DETAILS_ADMIN_NOTE =
  'To change these details, request an admin. The change has to be verified.'

export const PROFILE_DETAILS_MISSING_NOTE =
  'Your name, surname, or phone number is missing. Enter the missing details and save them. After you save, these details cannot be changed.'

const NAME_WORD = /^[A-Za-z][A-Za-z'\-]*$/

export function canSelfEditProfileDetails(profileDetailsEditUsed) {
  return profileDetailsEditUsed === false
}

function storedNamePartsPresent(profile) {
  return namePartIsPresent(profile?.first_name) && namePartIsPresent(profile?.last_name)
}

// The one-edit flag defaults to true, including for a brand-new account that
// has never saved from Settings. A real save always stores first_name and
// last_name. Until both are stored, the member still has that one save.
export function profileEditAcceptsAllFields(profile) {
  if (canSelfEditProfileDetails(profile?.profile_details_edit_used)) return true
  return !storedNamePartsPresent(profile)
}

export function selfServiceProfileEditOpen(profile) {
  if (profileEditAcceptsAllFields(profile)) return true
  return missingProfileDetailFields(profile).length > 0
}

export function namePartIsPresent(value) {
  const text = String(value || '').trim()
  if (text.length < 2 || text.length > 100) return false
  const words = text.split(/\s+/)
  return words.every((word) => word.length >= 2 && word.length <= 100 && NAME_WORD.test(word))
}

export function phoneIsPresent(phone) {
  const digits = String(phone || '').replace(/\D/g, '')
  return digits.length >= 7 && digits.length <= 15
}

export function splitStoredName(profile) {
  const storedFirst = String(profile?.first_name || '').trim()
  const storedLast = String(profile?.last_name || '').trim()
  const parts = String(profile?.full_name || '').trim().split(/\s+/).filter(Boolean)
  const name = namePartIsPresent(storedFirst) ? storedFirst : (parts[0] || '')
  const surnameFromFullName = parts.length >= 2 ? parts.slice(1).join(' ') : ''
  const surname = namePartIsPresent(storedLast) ? storedLast : surnameFromFullName
  return { name, surname }
}

export function missingProfileDetailFields(profile) {
  const { name, surname } = splitStoredName(profile || {})
  const missing = []
  if (!namePartIsPresent(name)) missing.push('name')
  if (!namePartIsPresent(surname)) missing.push('surname')
  if (!phoneIsPresent(profile?.phone)) missing.push('phone')
  if (!String(profile?.country_of_residence || '').trim()) missing.push('country')
  return missing
}

export function profileDetailsAreComplete(profile) {
  return missingProfileDetailFields(profile).length === 0
}
